<template>
  <BottomWrapper
    id="rule-engine-bottom-wrapper"
    v-waiting.center="isLoading = { isLoading: loading }"
    :buttons="buttons"
    cancelable-request
    @select="submit"
    @support-request:cancel="$emit('support-request:cancel', $event)"
  >
    <div class="form">
      <div class="center title-wrapper">
        <div>{{ $t('page.login.fill') }}</div>
        <div v-if="loginErrorFromApi" class="error-msg">
          <div class="err-img">
            <img :src="require('@/assets/img/error.png')" />
          </div>
          <div>{{ $t('page.login.invalid') }}</div>
        </div>
      </div>
      <div class="sd-input">
        <input
          v-model="form.username"
          name="username"
          :type="userFieldType.inputType"
          :class="[{ warn: error.username }]"
          :placeholder="`* ${userFieldType.name}`"
          @focus="error.username = ''"
        />
        <p v-if="error.username" class="warn-note-wrapper">
          <span class="star">*</span>
          {{ error.username }}
        </p>
      </div>
      <div class="sd-input">
        <input
          v-model="form.password"
          :class="[{ warn: error.password }]"
          name="password"
          type="password"
          :placeholder="`* ${t('global.password', 'Password')}`"
          @focus="error.password = ''"
        />
        <p v-if="error.password" class="warn-note-wrapper">
          <span class="star">*</span>
          {{ $t('page.login.password.empty') }}
          {{ error.password }}
        </p>
      </div>
    </div>
    <template #behind-button>
      <div
        v-if="showGuestLoginOption"
        v-track-click="BTN_EVTS.CONNECT_REQUEST_LOGIN_CONTINUE_AS_GUEST"
        class="guest"
        @click="$emit('guest')"
      >
        {{ t('page.login.continue_as_guest', 'Continue As Guest') }}
      </div>
    </template>
  </BottomWrapper>
</template>

<script lang="ts">
import { Component, Prop, Vue } from 'vue-property-decorator';
import { BottomWrapper } from '../BottomWrapper.vue';
import { Bridge } from '@/common/bridge';
import { namespace } from 'vuex-class';
import { isEmailValid, isPhoneValid } from '@/common/utils';
import waiting from '@/components/loading/waiting';
import { LoginResult, PleasedGuestUserInfo } from '@/common/types';
import * as rule from '@/common/lib/form-valid';
import LogPageEventMixin, { PAGE_TRACKING, PAGE_EVENTS } from '@/mixins/logPageEvent';
import { mixins } from 'vue-class-component';
import trackClick, { BTN_EVTS } from '@/directives/trackClick';
import { analytics } from '@/common/logs/EventLogger';

const userModule = namespace('user');

interface LoginForm {
  username: string;
  password: string;
}

@Component({
  components: {
    BottomWrapper,
  },
  directives: {
    waiting,
    trackClick,
  },
})
export default class LoginGuest extends mixins(LogPageEventMixin) {
  @Prop({ default: false }) readonly isGuestAllowed!: boolean;

  @userModule.State('guestUser') guestUser!: PleasedGuestUserInfo;
  @userModule.State('usernameFieldType') usernameFieldType!: 'phone' | 'email';

  @userModule.Action('sportyLoginSuccess') sportyLoginSuccess!: (payload: LoginResult) => Promise<void>;

  readonly [PAGE_TRACKING] = PAGE_EVENTS.CONNECT_REQUEST_LOGIN;
  readonly BTN_EVTS = BTN_EVTS;

  loading = false;
  loginErrorFromApi = false;

  form = {
    username: '',
    password: '',
  };

  error = {
    username: '',
    password: '',
  };

  get buttons() {
    return [
      {
        name: this.t('page.login.button.login', 'Login'),
        id: '',
        isActive: true,
      },
    ];
  }

  get showGuestLoginOption() {
    return this.isGuestAllowed && !this.guestUser.filledDetails;
  }

  get userFieldType() {
    return this.usernameFieldType === 'email'
      ? {
          type: 'email',
          name: this.$t('global.email'),
          inputType: 'email',
        }
      : {
          type: 'phone',
          name: this.t('page.login.mobile.placeholder', 'Mobile no'),
          inputType: 'tel',
        };
  }

  get loginFormSuite(): rule.ValidationSuite<LoginForm> {
    return {
      username: [
        rule.c(rule.required, '{uFieldName} is required'),
        rule.c(
          (val: string, _: any, ctx: any) => (ctx.uFieldType === 'phone' ? isPhoneValid(val) : isEmailValid(val)),
          'Please enter a valid {uFieldName}'
        ),
      ],
      password: [rule.c(rule.required, 'Password is required')],
    };
  }

  protected cleanPopListener: CallableFunction | null = null;

  created() {
    document.title = 'Login Guest';
    this.cleanPopListener = Bridge.listenFromParent('LOGIN_POP', async (data: LoginResult) => {
      this.loading = true;
      await this.onLoginSuccess(data);
    });
  }

  async submit() {
    this.loginErrorFromApi = false;
    this.loading = true;
    const { valid, errors } = await rule.validateForm(this.form, this.loginFormSuite, {
      uFieldType: this.userFieldType.type,
      uFieldName: this.userFieldType.name,
    });
    this.error = Object.assign({}, this.error, errors);
    analytics.trackClick({
      name: this.BTN_EVTS.CONNECT_REQUEST_LOGIN_CLICKED,
      metadata: { valid, errors },
    });

    if (!valid) {
      this.loading = false;
      return;
    }
    const data = await Bridge.login({ username: this.form.username, password: this.form.password });
    await this.onLoginSuccess(data);
    this.loading = false;
  }

  destroyed() {
    this.cleanPopListener?.();
  }

  async onLoginSuccess(data: LoginResult) {
    if (data.bizCode === 10000) {
      await this.sportyLoginSuccess(data).catch(() => {});
      this.$emit('login');
    } else {
      this.loginErrorFromApi = true;
    }
  }
}
</script>

<style lang="scss" scoped>
@import '@/assets/scss/variable.scss';
.guest {
  @extend .text-medium;
  color: $login-form-link-text;
  text-align: center;
  padding-top: 9px;
  text-decoration: underline;
  cursor: pointer;
}
</style>
